import { ReactComponent as LocationIcon } from "../../assets/img/profile/location-icon.svg";
import { ReactComponent as TitleRankIcon } from "../../assets/img/profile/title-rank-icon.svg";
import { ReactComponent as UniversityIcon } from "../../assets/img/profile/university-icon.svg";
import { ReactComponent as LinkedinIcon } from "../../assets/img/profile/linkedin-icon.svg";
import { ReactComponent as TwitterIcon } from "../../assets/img/profile/x-icon.svg";
import { ReactComponent as EmailIcon } from "../../assets/img/profile/email-icon.svg";
import { ReactComponent as WebsitesIcon } from "../../assets/img/profile/websites-icon.svg";
import MarkdownPreview from "@uiw/react-markdown-preview";
import LinkInfoItem from "../../components/common/LinkInfoItem";
import { convertHtmlToText } from "../../utils/general";
import { HiOutlineAcademicCap, HiOutlineBriefcase, HiOutlineMail, HiOutlineGlobe, HiOutlineStar } from "react-icons/hi";
import { FaLinkedinIn, FaXTwitter } from "react-icons/fa6";
import { BiTrash } from "react-icons/bi";

const InfoCard = ({ icon: Icon, title, value, className = "" }) => {
  if (!value) return null;
  return (
    <div className={`flex items-center gap-3 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 ${className}`}>
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/10 to-[#7e7cff]/10 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-primary" />
      </div>
      <div className="flex flex-col min-w-0">
        <span className="text-xs text-gray-500 font-medium">{title}</span>
        <span className="text-sm sm:text-base font-semibold text-secondary truncate">{value}</span>
      </div>
    </div>
  );
};

const SocialLink = ({ icon: Icon, url, label }) => {
  if (!url) return null;
  return (
    <a
      href={url.startsWith('http') ? url : `https://${url}`}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 p-3 bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 group"
    >
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/10 to-[#7e7cff]/10 flex items-center justify-center group-hover:from-primary group-hover:to-[#7e7cff] transition-all duration-300">
        <Icon className="w-5 h-5 text-primary group-hover:text-white transition-colors" />
      </div>
      <div className="flex flex-col min-w-0 flex-1">
        <span className="text-xs text-gray-500 font-medium">{label}</span>
        <span className="text-sm font-medium text-secondary truncate">{url}</span>
      </div>
    </a>
  );
};

const SectionCard = ({ title, subtitle, children, icon: Icon }) => {
  return (
    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
      <div className="p-6 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex items-center gap-3">
          {Icon && (
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-[#7e7cff] flex items-center justify-center">
              <Icon className="w-5 h-5 text-white" />
            </div>
          )}
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-secondary abel-font">{title}</h3>
            {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
          </div>
        </div>
      </div>
      <div className="p-6">
        {children}
      </div>
    </div>
  );
};

const ViewApplicantProfileLayout = ({
  userProfileData,
  handleSendVerifyEmail,
  sendEmailLoading,
  userId,
  onDeleteAccount,
}) => {
  const hasBiography = convertHtmlToText({ htmlContent: userProfileData?.biography });
  const hasExperiences = convertHtmlToText({ htmlContent: userProfileData?.experiences });
  const hasAccomplishments = convertHtmlToText({ htmlContent: userProfileData?.accomplishment });
  
  // Check if user signed up via OAuth (email pre-verified by provider)
  const isOAuthUser = userProfileData?.auth_provider && userProfileData?.auth_provider !== "wallet";
  const hasSocialLinks = userProfileData?.linkedIn || userProfileData?.x || userProfileData?.email;
  const hasWebsites = userProfileData?.websites?.length > 0;

  return (
    <div className="w-full flex flex-col gap-6 sm:gap-8">
      {/* Quick Info Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <InfoCard 
          icon={HiOutlineAcademicCap} 
          title="Title / Position" 
          value={userProfileData?.title_rank} 
        />
        <InfoCard 
          icon={HiOutlineBriefcase} 
          title="University / Institution" 
          value={userProfileData?.university_college} 
        />
        <InfoCard 
          icon={LocationIcon} 
          title="Nationality" 
          value={userProfileData?.nationality} 
        />
      </div>

      {/* Biography Section */}
      {hasBiography && (
        <SectionCard title="About Me" icon={HiOutlineStar}>
          <MarkdownPreview
            source={userProfileData?.biography}
            style={{ background: "transparent" }}
            wrapperElement={{
              "data-color-mode": "light",
            }}
            className="prose prose-sm sm:prose max-w-none"
          />
        </SectionCard>
      )}

      {/* Contact & Social Section */}
      {(hasSocialLinks || hasWebsites) && (
        <SectionCard title="Connect" subtitle="Social links and contact information" icon={HiOutlineGlobe}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <SocialLink icon={FaLinkedinIn} url={userProfileData?.linkedIn} label="LinkedIn" />
            <SocialLink icon={FaXTwitter} url={userProfileData?.x} label="X (Twitter)" />
            
            {/* Email - only show for own profile */}
            {!userId && userProfileData?.email && (
              <div className="flex items-center gap-3 p-3 bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/10 to-[#7e7cff]/10 flex items-center justify-center">
                  <HiOutlineMail className="w-5 h-5 text-primary" />
                </div>
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="text-xs text-gray-500 font-medium">Email</span>
                  <div className="flex items-center gap-2">
                    <a href={`mailto:${userProfileData?.email}`} className="text-sm font-medium text-secondary truncate hover:text-primary transition-colors">
                      {userProfileData?.email}
                    </a>
                    {userProfileData?.email_verified ? (
                      <span className="px-2 py-0.5 text-[10px] font-bold bg-green-100 text-green-600 rounded-full">
                        {isOAuthUser ? `Verified via ${userProfileData?.auth_provider}` : 'Verified'}
                      </span>
                    ) : (
                      <button
                        onClick={handleSendVerifyEmail}
                        disabled={sendEmailLoading}
                        className="px-2 py-0.5 text-[10px] font-bold bg-yellow-100 text-yellow-700 rounded-full hover:bg-yellow-200 transition-colors"
                      >
                        {sendEmailLoading ? "Sending..." : "Verify"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Verified badge for public profiles */}
            {userId && userProfileData?.email_verified && (
              <div className="flex items-center gap-3 p-3 bg-white rounded-xl shadow-sm">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-100 to-green-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="text-xs text-gray-500 font-medium">Status</span>
                  <span className="text-sm font-medium text-emerald-600">Verified Profile</span>
                </div>
              </div>
            )}
            
            {/* Websites */}
            {userProfileData?.websites?.map((website, index) => (
              <SocialLink key={index} icon={HiOutlineGlobe} url={website} label={`Website ${index + 1}`} />
            ))}
          </div>
          
          {!userId && !userProfileData?.email_verified && !isOAuthUser && (
            <div className="mt-4 p-4 bg-yellow-50 rounded-xl shadow-sm">
              <p className="text-sm text-yellow-700">
                ⚠️ Please verify your email address to access all FunDeSci features.
              </p>
            </div>
          )}
        </SectionCard>
      )}

      {/* Experiences Section */}
      {hasExperiences && (
        <SectionCard 
          title="Experience" 
          subtitle="Projects, Publications, Awards, and more"
          icon={HiOutlineBriefcase}
        >
          <MarkdownPreview
            source={userProfileData?.experiences}
            style={{ background: "transparent" }}
            wrapperElement={{
              "data-color-mode": "light",
            }}
            className="prose prose-sm sm:prose max-w-none"
          />
        </SectionCard>
      )}

      {/* Accomplishments Section */}
      {hasAccomplishments && (
        <SectionCard 
          title="Recent Accomplishments" 
          subtitle="Achievements from the last 5 years"
          icon={HiOutlineStar}
        >
          <MarkdownPreview
            source={userProfileData?.accomplishment}
            style={{ background: "transparent" }}
            wrapperElement={{
              "data-color-mode": "light",
            }}
            className="prose prose-sm sm:prose max-w-none"
          />
        </SectionCard>
      )}

      {/* Danger Zone - Only show on own profile */}
      {!userId && onDeleteAccount && (
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden border-2 border-red-100">
          <div className="p-6 bg-gradient-to-r from-red-50 to-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                <BiTrash className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-red-600 abel-font">Danger Zone</h3>
                <p className="text-sm text-gray-500">Permanent account deletion</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 p-4 bg-red-50 rounded-xl">
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 mb-1">Delete Your Account</h4>
                <p className="text-sm text-gray-600">
                  Once you delete your account, there is no going back. Please be certain.
                </p>
              </div>
              <button
                onClick={onDeleteAccount}
                className="px-6 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-all duration-300 hover:shadow-lg flex items-center gap-2 whitespace-nowrap"
              >
                <BiTrash size={18} />
                Delete Account
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewApplicantProfileLayout;
